(() => {
  const SCRIPT_KEY = "bsbEsptoolFlasherLoaded";

  if (window[SCRIPT_KEY]) {
    initAllSections();
    document.addEventListener("shopify:section:load", initAllSections);
    return;
  }

  window[SCRIPT_KEY] = true;

  let espToolModulePromise = null;

  async function loadEspTool() {
    if (!espToolModulePromise) {
      espToolModulePromise = import("https://unpkg.com/esptool-js@0.6.0/bundle.js");
    }
    return espToolModulePromise;
  }

  function initAllSections() {
    document.querySelectorAll(".bsb-esptool-flasher").forEach((section) => {
      if (section.dataset.esptoolReady === "true") return;
      section.dataset.esptoolReady = "true";
      new BSBEsptoolFlasher(section);
    });
  }

  class BSBEsptoolFlasher {
    constructor(section) {
      this.section = section;
      this.port = null;
      this.transport = null;
      this.loader = null;
      this.connected = false;
      this.isBusy = false;

      this.statusEl = section.querySelector("[data-status]");
      this.logEl = section.querySelector("[data-log]");
      this.logWrapEl = section.querySelector("[data-log-wrap]");

      this.connectBtn = section.querySelector('[data-action="connect"]');
      this.clearLogBtn = section.querySelector('[data-action="clear-log"]');
      this.flashBtns = [...section.querySelectorAll('[data-action="flash"]')];
      this.selects = [...section.querySelectorAll("[data-firmware-select]")];
      this.deviceToggles = [...section.querySelectorAll("[data-device-toggle]")];
      this.logToggles = [...section.querySelectorAll("[data-log-toggle]")];

      this.bindEvents();
      this.initializeCards();
      this.syncLogToggleState();
      this.log("Ready.");
      this.log("Use Chrome or Edge on desktop.");
      this.log("Click Connect Device to begin.");
    }

    bindEvents() {
      this.connectBtn?.addEventListener("click", () => this.handleConnect());
      this.clearLogBtn?.addEventListener("click", () => this.clearLog());

      this.flashBtns.forEach((btn) => {
        btn.addEventListener("click", () => this.handleFlash(btn));
      });

      this.selects.forEach((select) => {
        select.addEventListener("change", () => this.syncCardSelection(select));
      });

      this.deviceToggles.forEach((toggle) => {
        toggle.addEventListener("click", () => this.toggleDeviceCard(toggle));
      });

      this.logToggles.forEach((toggle) => {
        toggle.addEventListener("change", () => this.handleLogToggleChange(toggle));
      });
    }

    initializeCards() {
      this.selects.forEach((select) => this.syncCardSelection(select));
    }

    handleLogToggleChange(changedToggle) {
      const checked = !!changedToggle.checked;
      this.logToggles.forEach((toggle) => {
        toggle.checked = checked;
      });
      this.syncLogWrapVisibility();
    }

    syncLogToggleState() {
      const checked = this.logToggles.some((toggle) => toggle.checked);
      this.logToggles.forEach((toggle) => {
        toggle.checked = checked;
      });
      this.syncLogWrapVisibility();
    }

    syncLogWrapVisibility() {
      if (!this.logWrapEl) return;
      const checked = this.logToggles.some((toggle) => toggle.checked);
      this.logWrapEl.hidden = !checked;
    }

    toggleDeviceCard(toggle) {
      const card = toggle.closest("[data-device-card]");
      const panel = card?.querySelector("[data-device-panel]");
      if (!card || !panel) return;

      const isOpen = card.classList.contains("is-open");

      this.section.querySelectorAll("[data-device-card]").forEach((otherCard) => {
        const otherToggle = otherCard.querySelector("[data-device-toggle]");
        const otherPanel = otherCard.querySelector("[data-device-panel]");

        otherCard.classList.remove("is-open");
        if (otherToggle) otherToggle.setAttribute("aria-expanded", "false");
        if (otherPanel) otherPanel.hidden = true;
      });

      if (!isOpen) {
        card.classList.add("is-open");
        toggle.setAttribute("aria-expanded", "true");
        panel.hidden = false;
      }
    }

    syncCardSelection(select) {
      const card = select.closest("[data-device-card]");
      if (!card) return;

      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) return;

      const flashBtn = card.querySelector('[data-action="flash"]');
      const addressEl = card.querySelector("[data-selected-address]");
      const baudEl = card.querySelector("[data-selected-baud]");
      const downloadEl = card.querySelector("[data-selected-download]");

      const title = selectedOption.dataset.title || selectedOption.textContent.trim();
      const url = selectedOption.dataset.url || "";
      const address = selectedOption.dataset.address || "0x0000";
      const baud = selectedOption.dataset.baud || "921600";
      const compress = selectedOption.dataset.compress === "true";
      const eraseFirst = selectedOption.dataset.eraseFirst === "true";

      if (flashBtn) {
        flashBtn.dataset.title = title;
        flashBtn.dataset.url = url;
        flashBtn.dataset.address = address;
        flashBtn.dataset.baud = baud;
        flashBtn.dataset.compress = compress ? "true" : "false";
        flashBtn.dataset.eraseFirst = eraseFirst ? "true" : "false";
      }

      if (addressEl) addressEl.textContent = address;
      if (baudEl) baudEl.textContent = baud;

      if (downloadEl) {
        downloadEl.href = url || "#";
        downloadEl.style.display = url ? "" : "none";
      }
    }

    setBusy(busy) {
      this.isBusy = busy;
      const disabled = !!busy;

      [this.connectBtn, ...this.flashBtns, ...this.selects].forEach((el) => {
        if (el) el.disabled = disabled;
      });
    }

    setStatus(text) {
      if (this.statusEl) this.statusEl.textContent = text;
    }

    log(message) {
      const text = String(message);
      const timestamp = new Date().toLocaleTimeString();
      this.logEl.textContent += `[${timestamp}] ${text}\n`;
      this.logEl.scrollTop = this.logEl.scrollHeight;
    }

    clearLog() {
      this.logEl.textContent = "";
      this.log("Log cleared.");
    }

    async handleConnect() {
      if (this.isBusy) return;

      if (!("serial" in navigator)) {
        this.log("This browser does not support Web Serial.");
        this.setStatus("Web Serial not supported");
        return;
      }

      this.setBusy(true);
      this.setStatus("Connecting...");

      try {
        const { ESPLoader, Transport } = await loadEspTool();

        this.log("Requesting serial port...");
        this.port = await navigator.serial.requestPort({});

        this.transport = new Transport(this.port, true);

        const loaderTerminal = {
          clean: () => {},
          writeLine: (data) => this.log(data),
          write: (data) => this.log(data)
        };

        this.loader = new ESPLoader({
          transport: this.transport,
          baudrate: 115200,
          terminal: loaderTerminal,
          debugLogging: false
        });

        this.log("Connecting to chip...");
        const chip = await this.loader.main();

        this.connected = true;
        this.setStatus(`Connected: ${chip || "ESP device"}`);
        this.log(`Connected to: ${chip || "ESP device"}`);
      } catch (error) {
        this.log(`Connect failed: ${this.formatError(error)}`);
        this.setStatus("Connect failed");
        this.connected = false;
        await this.safeDisconnect();
      } finally {
        this.setBusy(false);
      }
    }

    async ensureConnected() {
      if (this.connected && this.loader && this.transport) return true;
      this.log("No active device connection.");
      this.setStatus("Not connected");
      return false;
    }

    async handleFlash(button) {
      if (this.isBusy) return;
      if (!(await this.ensureConnected())) return;

      const title = button.dataset.title || "Firmware";
      const firmwareUrl = button.dataset.url || "";
      const address = button.dataset.address || "0x0000";
      const compress = button.dataset.compress === "true";
      const eraseFirst = button.dataset.eraseFirst === "true";

      if (!firmwareUrl) {
        this.log(`No firmware URL set for "${title}".`);
        this.setStatus("Missing firmware URL");
        return;
      }

      this.setBusy(true);
      this.setStatus(`Downloading ${title}...`);

      try {
        this.log(`Fetching firmware: ${firmwareUrl}`);
        const response = await fetch(firmwareUrl, { cache: "no-store" });

        if (!response.ok) {
          throw new Error(`Firmware download failed (${response.status})`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const firmwareData = new Uint8Array(arrayBuffer);

        this.log(`Downloaded ${firmwareData.length.toLocaleString()} bytes.`);

        if (eraseFirst) {
          this.setStatus("Erasing...");
          this.log("Erasing flash before install...");
          await this.loader.eraseFlash();
        }

        this.setStatus(`Flashing ${title}...`);
        this.log(`Writing image to ${address}...`);

        await this.loader.writeFlash({
          fileArray: [
            {
              data: firmwareData,
              address: this.parseAddress(address)
            }
          ],
          flashSize: "keep",
          flashMode: "keep",
          flashFreq: "keep",
          eraseAll: false,
          compress,
          reportProgress: (_fileIndex, written, total) => {
            const percent = total > 0 ? (written / total) * 100 : 0;

            if (percent < 99) {
              this.setStatus(`Flashing ${title}... ${Math.floor(percent)}%`);
            } else {
              this.setStatus("Finalizing...");
            }
          }
        });

        this.setStatus("Resetting device...");
        this.log(`Flash complete: ${title}`);

        if (typeof this.loader.after === "function") {
          try {
            await this.loader.after("hard_reset");
            this.log("Device reset complete.");
          } catch (resetError) {
            this.log(`Reset warning: ${this.formatError(resetError)}`);
          }
        } else {
          this.log("Flash finished. Reboot the device if needed.");
        }

        this.setStatus("Flash complete - You can now reboot your miner.");
      } catch (error) {
        this.log(`Flash failed: ${this.formatError(error)}`);
        this.setStatus("Flash failed");
      } finally {
        this.setBusy(false);
      }
    }

    parseAddress(value) {
      const raw = String(value).trim().toLowerCase();
      if (raw.startsWith("0x")) return parseInt(raw, 16);
      return parseInt(raw, 10);
    }

    formatError(error) {
      if (!error) return "Unknown error";
      if (typeof error === "string") return error;
      return error.message || String(error);
    }

    async safeDisconnect() {
      this.connected = false;

      try {
        if (this.transport && typeof this.transport.disconnect === "function") {
          await this.transport.disconnect();
        }
      } catch (_) {}

      try {
        if (this.port && typeof this.port.close === "function") {
          await this.port.close();
        }
      } catch (_) {}

      this.port = null;
      this.transport = null;
      this.loader = null;
    }
  }

  initAllSections();
  document.addEventListener("shopify:section:load", initAllSections);
})();